import React, { useCallback, useEffect, useState } from 'react';

import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import lodash from 'lodash';

const withAutoSuggestFormik = (AutoSuggestComponent) => {
   const WrappedComponent = (props) => {
      const { values, setFieldValue, touched } = useFormikContext();
      const [autosuggestProps, setAutosuggestProps] = useState({});
      const [isTouched, setIsTouched] = useState(false);
      const [errorMessage, setErrorMessage] = useState();
      const strValues = JSON.stringify(values);

      useEffect(() => {
         // Used for getting the errorMessage when NumberInput component is used as a FieldArray Field
         const nameParts = props.field.name.split(/\]\.|\.|\[|\]/);

         let errorPtr = props.form.errors;

         if (errorPtr) {
            for (let i = 0; i < nameParts.length; i++) {
               const name = nameParts[i];
               if (lodash.isObject(errorPtr)) {
                  errorPtr = errorPtr[name];
               } else {
                  break;
               }
            }
         }

         if (typeof errorPtr === 'string' || typeof errorPtr === 'undefined') {
            setErrorMessage(errorPtr);
         } else if (lodash.isObject(errorPtr) && errorPtr?.id === 'Required!') {
            setErrorMessage('Please select a value from the autosuggest.');
         }
      }, [JSON.stringify(props.form.errors)]);

      // when values changes autoSuggest dependencyProp values
      useEffect(() => {
         const dependsOn = props.autosuggestProps?.dependsOn;

         if (lodash.isObject(dependsOn) && !lodash.isEmpty(dependsOn)) {
            const newAutosuggestProps = {};

            for (const [key, value] of Object.entries(values)) {
               if (dependsOn.hasOwnProperty(key)) {
                  const [keyDependency, valueDependency] = dependsOn[key](value);
                  newAutosuggestProps[keyDependency] = valueDependency;
               }
            }

            setAutosuggestProps(newAutosuggestProps);
         }
      }, [strValues]);

      const handleChange = useCallback((selectedOption) => {
         setFieldValue(props.field.name, selectedOption);
      }, [props.name]);

      const onBlur = useCallback(() => {
         props.form.setFieldTouched(props.field.name, true);
      }, []);

      useEffect(() => {
         const isFieldTouched = touched[props.field.name];

         if (lodash.isObject(isFieldTouched)) {
            for (const [key, value] of Object.entries(isFieldTouched)) {
               if (value === true) {
                  setIsTouched(true);
                  break;
               }
            }
         } else if (lodash.isBoolean(isFieldTouched)) {
            setIsTouched(isFieldTouched);
         }
      }, [JSON.stringify(touched)]);

      return (
         <AutoSuggestComponent
            onSelection={handleChange}
            onBlur={onBlur}
            disabled={props.isDisabled}
            selected={props.field.value}
            isTouched={isTouched}
            errorMessage={errorMessage}
            className={props.className}
            {...autosuggestProps}
         />
      );
   };

   WrappedComponent.propTypes = {
      field: PropTypes.shape({
         name: PropTypes.string.isRequired,
         onChange: PropTypes.func.isRequired,
         onBlur: PropTypes.func.isRequired,
         value: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string.isRequired,
         }),
      }).isRequired,
      form: PropTypes.shape({
         setFieldTouched: PropTypes.func.isRequired,
         touched: PropTypes.object.isRequired,
         errors: PropTypes.object.isRequired,
      }).isRequired,
      autosuggestProps: PropTypes.shape({
         dependsOn: PropTypes.objectOf(PropTypes.func).isRequired,
      }),
      isDisabled: PropTypes.bool,
      className: PropTypes.string,
   };

   WrappedComponent.defaultProps = {
      className: '',
      autosuggestProps: undefined,
   };

   return WrappedComponent;
};

export default withAutoSuggestFormik;
